import { AlertCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface DispatchInlineStateProps {
  title: string;
  description?: string;
  loading?: boolean;
  tone?: "default" | "error";
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
}

export function DispatchInlineState({
  title,
  description,
  loading = false,
  tone = "default",
  actionLabel,
  onAction,
  className,
}: DispatchInlineStateProps) {
  return (
    <Card
      role={tone === "error" ? "alert" : undefined}
      className={cn(
        "border-dashed shadow-none",
        tone === "error" && "border-red-200 bg-red-50",
        className
      )}
    >
      <CardContent className="space-y-2 p-3">
        {loading ? (
          <div role="status" aria-label={title} className="space-y-2">
            <span className="sr-only">{title}</span>
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-4/5" />
          </div>
        ) : (
          <div className="flex items-start gap-2">
            {tone === "error" && (
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            )}
            <div className="min-w-0">
              <p
                className={cn(
                  "text-sm font-medium text-zinc-700",
                  tone === "error" && "text-red-700"
                )}
              >
                {title}
              </p>
              {description && (
                <p className="mt-1 text-xs text-zinc-500">{description}</p>
              )}
            </div>
          </div>
        )}
        {actionLabel && onAction && (
          <Button type="button" variant="outline" size="sm" onClick={onAction}>
            {actionLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
